// ensureI18nLoadedHelper.js
'use strict';

import i18n from '../resource/i18n.js';

const namespaceLoadPromiseMap = new Map();

/**
 * Ensure a lazy-loaded i18n namespace/key is ready before rendering UI.
 *
 * @param {object} params
 * @param {string} params.ns - i18n namespace
 * @param {string} params.key - translation key used to verify readiness
 * @param {number} [params.timeoutMsec=3000] - fallback timeout
 * @returns {Promise<boolean>} whether the key exists after waiting
 */
const ensureI18nLoadedHelper = async ({ ns, key, timeoutMsec = 3000 } = {}) => {
  if (!ns || !key) {
    return false;
  }

  if (i18n.customExists?.(key, { ns })) {
    return true;
  }

  const cacheKey = `${ns}-${key}`;

  if (!namespaceLoadPromiseMap.has(cacheKey)) {
    const loadPromise = new Promise(resolve => {
      let timer = null;

      const finish = () => {
        if (timer) {
          clearTimeout(timer);
        }
        i18n.store?.off?.('added', handleAdded);
        namespaceLoadPromiseMap.delete(cacheKey);
        resolve(i18n.customExists?.(key, { ns }) || false);
      };

      const handleAdded = (_language, addedNamespace) => {
        if (addedNamespace !== ns) return;
        finish();
      };

      i18n.store?.on?.('added', handleAdded);

      if (i18n.customExists?.(key, { ns })) {
        finish();
        return;
      }

      timer = setTimeout(() => {
        finish();
      }, timeoutMsec);
    });

    namespaceLoadPromiseMap.set(cacheKey, loadPromise);
  }

  return namespaceLoadPromiseMap.get(cacheKey);
};

export default ensureI18nLoadedHelper;
